import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingDown, BarChart3, Target, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Analytics() {
  const [specialtyData, setSpecialtyData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [specialtyRes, timelineRes, alertsRes] = await Promise.all([
        api.get('/analytics/by-specialty'),
        api.get('/analytics/progress-timeline'),
        api.get('/analytics/alerts')
      ]);
      setSpecialtyData(specialtyRes.data);
      setTimelineData(timelineRes.data);
      setAlerts(alertsRes.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="analytics-page">
      <div className="max-w-md mx-auto md:max-w-6xl px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold tracking-tight">Analytics</h1>
          </div>
          <p className="text-lg text-slate-600">Análise detalhada do seu desempenho</p>
        </div>

        {alerts.length > 0 && (
          <Card className="mb-6 shadow-sm border-l-4 border-l-orange-500 bg-white dark:bg-slate-900" data-testid="alerts-summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl text-orange-900 dark:text-orange-200">
                <AlertCircle className="w-5 h-5" />
                Alertas de Desempenho ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.map((alert, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.severity === 'critical'
                      ? 'bg-red-50 dark:bg-red-900/10 border-l-red-500'
                      : 'bg-blue-50 dark:bg-blue-900/10 border-l-blue-500'
                  }`}
                  data-testid={`alert-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-5 h-5 mt-0.5 ${
                      alert.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                    }`} />
                    <div className="flex-1">
                      <h4 className={`font-semibold mb-1 ${
                        alert.severity === 'critical' 
                          ? 'text-red-900 dark:text-red-200' 
                          : 'text-yellow-900 dark:text-yellow-200'
                      }`}>
                        {alert.specialty}: {alert.correct_rate}% de acerto
                      </h4>
                      <p className={`text-sm ${
                        alert.severity === 'critical'
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {alert.recommendation}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${
                      alert.severity === 'critical'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}>
                      {alert.severity === 'critical' ? 'CRÍTICO' : 'ATENÇÃO'}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="mb-6 shadow-sm bg-white dark:bg-slate-900" data-testid="progress-timeline-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <TrendingDown className="w-5 h-5 text-blue-600" />
              Evolução da Taxa de Erros ao Longo do Tempo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {timelineData.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">Nenhum dado disponível ainda</p>
                <p className="text-sm text-slate-500 mt-1">Complete alguns temas com questões para ver seu progresso</p>
              </div>
            ) : (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={timelineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getDate()}/${date.getMonth() + 1}`;
                      }}
                    />
                    <YAxis 
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                      label={{ value: 'Taxa (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'error_rate') return [`${value}%`, 'Taxa de Erros'];
                        if (name === 'correct_rate') return [`${value}%`, 'Taxa de Acertos'];
                        return [value, name];
                      }}
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('pt-BR');
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '14px' }}
                      formatter={(value) => {
                        if (value === 'error_rate') return 'Taxa de Erros';
                        if (value === 'correct_rate') return 'Taxa de Acertos';
                        return value;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="error_rate" 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      dot={{ fill: '#ef4444', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Taxa de Erros"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="correct_rate" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Taxa de Acertos"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    <strong>Dica:</strong> Uma linha de erros descendente indica progresso! Continue praticando nas áreas com maior taxa de erro.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6 shadow-sm bg-white dark:bg-slate-900" data-testid="specialty-stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Target className="w-5 h-5 text-blue-600" />
              Desempenho por Especialidade Médica
            </CardTitle>
          </CardHeader>
          <CardContent>
            {specialtyData.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">Nenhuma especialidade cadastrada</p>
                <p className="text-sm text-slate-500 mt-1">Adicione temas de diferentes especialidades para ver as estatísticas</p>
              </div>
            ) : (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={specialtyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="specialty" 
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      stroke="#64748b"
                      style={{ fontSize: '12px' }}
                      label={{ value: 'Taxa de Acerto (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'correct_rate') return [`${value}%`, 'Taxa de Acerto'];
                        return [value, name];
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '14px' }}
                      formatter={(value) => {
                        if (value === 'correct_rate') return 'Taxa de Acerto';
                        return value;
                      }}
                    />
                    <Bar 
                      dataKey="correct_rate" 
                      fill="#3b82f6" 
                      radius={[8, 8, 0, 0]}
                      name="Taxa de Acerto"
                    />
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-6 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-semibold">Especialidade</th>
                        <th className="text-center py-3 px-2 font-semibold">Questões</th>
                        <th className="text-center py-3 px-2 font-semibold">Acertos</th>
                        <th className="text-center py-3 px-2 font-semibold">Erros</th>
                        <th className="text-center py-3 px-2 font-semibold">Taxa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {specialtyData
                        .sort((a, b) => b.correct_rate - a.correct_rate)
                        .map((item, index) => (
                          <tr key={index} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`specialty-row-${index}`}>
                            <td className="py-3 px-2 font-medium">{item.specialty}</td>
                            <td className="text-center py-3 px-2">{item.total_questions}</td>
                            <td className="text-center py-3 px-2 text-green-600 font-medium">{item.total_correct}</td>
                            <td className="text-center py-3 px-2 text-red-600 font-medium">{item.total_wrong}</td>
                            <td className="text-center py-3 px-2">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                item.correct_rate >= 70 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' 
                                  : item.correct_rate >= 50
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {item.correct_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    <strong>Interpretação:</strong> Foque mais tempo nas especialidades com taxa de acerto abaixo de 70%. 
                    Especialidades acima de 80% indicam domínio do conteúdo.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
